import {
  ArrowRight,
  CalendarCheck2,
  ChevronRight,
  UsersRound,
} from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppointmentDetailModal } from '../../appointments/components/AppointmentDetailModal'
import { AppointmentStatusBadge } from '../../appointments/components/AppointmentStatusBadge'
import type { AppointmentItem } from '../../appointments/appointments-model'
import { Card } from '../../../ui/Card/Card'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import type { TodayScheduleState } from '../use-today-schedule'
import styles from '../OverviewPage.module.css'

function scheduleTime(appointment: AppointmentItem, timeZone: string) {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(appointment.scheduledAtMs ?? 0)
}

export function TodaySchedule({
  schedule,
  nowMs,
  tenantId,
  renderAppointmentDetail,
}: {
  schedule: TodayScheduleState
  nowMs: number
  tenantId: string
  renderAppointmentDetail?: (
    appointment: AppointmentItem,
    onClose: () => void
  ) => ReactNode
}) {
  const navigate = useNavigate()
  const [provider, setProvider] = useState('all')
  const [selected, setSelected] = useState<AppointmentItem | null>(null)
  const panel = useRef<HTMLDivElement>(null)
  const [reservedHeight, setReservedHeight] = useState<number>()
  const upcomingItems = useMemo(
    () =>
      schedule.items.filter(
        (appointment) =>
          appointment.status !== 'cancelled' &&
          appointment.scheduledAtMs !== null &&
          appointment.scheduledAtMs >= nowMs
      ),
    [nowMs, schedule.items]
  )
  const providerNames = useMemo(
    () =>
      Array.from(
        new Set(
          upcomingItems.flatMap((appointment) =>
            appointment.providerName ? [appointment.providerName] : []
          )
        )
      ).sort((left, right) => left.localeCompare(right)),
    [upcomingItems]
  )
  const selectedProvider = providerNames.includes(provider) ? provider : 'all'
  const rows =
    selectedProvider === 'all'
      ? upcomingItems
      : upcomingItems.filter(
          (appointment) => appointment.providerName === selectedProvider
        )
  const visibleRows = rows.slice(0, 6)

  return (
    <>
      <div ref={panel}>
        <Card
          className={`${styles.panel} ${styles.schedulePanel}`}
          style={{ minHeight: reservedHeight }}
        >
          <header className={styles.panelHeader}>
            <div>
              <span className={styles.sectionLabel}>Coming up</span>
              <h3>Up next</h3>
              {schedule.status === 'ready' ? (
                <p>
                  {upcomingItems.length.toLocaleString()} remaining appointment
                  {upcomingItems.length === 1 ? '' : 's'}
                  {schedule.bounds ? ` · ${schedule.bounds.timeZone}` : ''}
                </p>
              ) : null}
            </div>
            {providerNames.length > 0 ? (
              <label className={styles.providerFilter}>
                <UsersRound size={14} aria-hidden="true" />
                <span className={styles.visuallyHidden}>
                  Filter by provider
                </span>
                <select
                  aria-label="Filter today's schedule by provider"
                  value={selectedProvider}
                  onChange={(event) => {
                    setReservedHeight(
                      panel.current?.getBoundingClientRect().height
                    )
                    setProvider(event.currentTarget.value)
                  }}
                >
                  <option value="all">All providers</option>
                  {providerNames.map((name) => (
                    <option value={name} key={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </header>

          {schedule.status === 'loading' ? (
            <div
              className={styles.rowsLoading}
              aria-label="Loading today's schedule"
            >
              {[0, 1, 2, 3].map((row) => (
                <span key={row} />
              ))}
            </div>
          ) : null}
          {schedule.status === 'forbidden' ? (
            <div className={styles.compactPanelState}>
              <StatePanel
                kind="forbidden"
                title="Schedule unavailable"
                description="You do not have permission to view this clinic schedule."
              />
            </div>
          ) : null}
          {schedule.status === 'error' || schedule.status === 'invalid' ? (
            <div className={styles.compactPanelState}>
              <StatePanel
                kind="error"
                title="Schedule unavailable"
                description="Today's projected appointment data could not be loaded safely."
              />
            </div>
          ) : null}
          {schedule.status === 'ready' && schedule.items.length === 0 ? (
            <div className={styles.quietState}>
              <CalendarCheck2 size={22} aria-hidden="true" />
              <strong>No appointments today</strong>
              <p>
                Projected appointments for this clinic day will appear here.
              </p>
            </div>
          ) : null}
          {schedule.status === 'ready' && schedule.items.length > 0 ? (
            rows.length ? (
              <ul className={styles.scheduleList}>
                {visibleRows.map((appointment) => (
                  <li key={appointment.appointmentId}>
                    <button
                      type="button"
                      onClick={() => setSelected(appointment)}
                      aria-label={`Open appointments for ${appointment.patientDisplayName}`}
                    >
                      <time
                        dateTime={
                          appointment.scheduledAtMs
                            ? new Date(appointment.scheduledAtMs).toISOString()
                            : undefined
                        }
                      >
                        {scheduleTime(
                          appointment,
                          schedule.bounds?.timeZone ?? 'UTC'
                        )}
                      </time>
                      <span className={styles.scheduleIdentity}>
                        <span className={styles.patientLine}>
                          <strong>{appointment.patientDisplayName}</strong>
                        </span>
                        {appointment.appointmentTypeName ? (
                          <span className={styles.scheduleAppointmentType}>
                            {appointment.appointmentTypeName}
                          </span>
                        ) : null}
                      </span>
                      <span className={styles.scheduleProvider}>
                        <strong>{appointment.providerName ?? '—'}</strong>
                        {appointment.operatoryName ? (
                          <span>{appointment.operatoryName}</span>
                        ) : null}
                      </span>
                      <span className={styles.scheduleStatus}>
                        {appointment.status !== 'unknown' ? (
                          <AppointmentStatusBadge appointment={appointment} />
                        ) : null}
                        <ChevronRight size={15} aria-hidden="true" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={styles.quietState}>
                <CalendarCheck2 size={22} aria-hidden="true" />
                <strong>
                  {selectedProvider === 'all'
                    ? 'No more appointments today'
                    : 'No upcoming appointments for this provider'}
                </strong>
                <p>
                  {selectedProvider === 'all'
                    ? 'The observed appointment-start workload is complete.'
                    : 'Choose All providers to restore the full upcoming list.'}
                </p>
              </div>
            )
          ) : null}
          {schedule.status === 'ready' ? (
            <footer className={styles.panelFooter}>
              <div className={styles.panelFooterRow}>
                {rows.length > 6 ? (
                  <p className={styles.scheduleSummary}>
                    Showing 6 of {rows.length.toLocaleString()} appointments
                  </p>
                ) : null}
                <button
                  type="button"
                  className={styles.panelAction}
                  onClick={() => void navigate('/appointments')}
                >
                  View full schedule <ArrowRight size={14} aria-hidden="true" />
                </button>
              </div>
              <p className={styles.scopeNote}>
                Up Next includes future, non-cancelled starts from the latest
                complete NexHealth clinic-day snapshot.
              </p>
            </footer>
          ) : null}
        </Card>
      </div>
      {selected ? (
        renderAppointmentDetail ? (
          renderAppointmentDetail(selected, () => setSelected(null))
        ) : (
          <AppointmentDetailModal
            key={selected.appointmentId}
            appointment={selected}
            tenantId={tenantId}
            nowMs={nowMs}
            onClose={() => setSelected(null)}
            onNavigate={(path) => void navigate(path)}
          />
        )
      ) : null}
    </>
  )
}
